import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, Eye, Heart, MessageCircle, Sparkles, Newspaper, GraduationCap, Stethoscope, Clock, Play } from "lucide-react";
import { API_URL } from "../config";
import { SPECIALTIES } from "../SpecialtyContext";
import { useAuth } from "../AuthContext";

const KIND_CONFIG = {
  post:    { path: "articles", label: "Статья", icon: Newspaper },
  case:    { path: "cases", label: "Случай", icon: Stethoscope },
  lecture: { path: "lectures", label: "Материал", icon: GraduationCap },
};

function Stat({ value, label, accent }) {
  return (
    <div className="author-stat">
      <div className="author-stat-value" style={accent ? { color: "var(--magenta)" } : undefined}>{value}</div>
      <div className="author-stat-label">{label}</div>
    </div>
  );
}

function VideoCard({ item }) {
  return (
    <Link to={`/lectures/${item.id}`} className="author-video">
      <span className="author-video-thumb">
        {item.thumbnail_url ? <img src={item.thumbnail_url} alt="" loading="lazy" /> : <span className="lecture-thumb-fallback" />}
        <span className="lecture-play"><Play size={20} fill="currentColor" strokeWidth={0} /></span>
        {item.duration && <span className="lecture-duration"><Clock size={12} strokeWidth={2.4} /> {item.duration}</span>}
      </span>
      <span className="author-video-title">{item.title}</span>
      <span className="author-video-meta">
        {item.material_type && <span>{item.material_type}</span>}
        <span><Eye size={12} strokeWidth={2.2} /> {item.views}</span>
      </span>
    </Link>
  );
}

function TextRow({ item }) {
  const cfg = KIND_CONFIG[item.kind];
  const Icon = cfg.icon;
  return (
    <Link to={`/${cfg.path}/${item.id}`} className="author-row">
      <Icon size={15} strokeWidth={2.2} color="#e5005b" style={{ flexShrink: 0 }} />
      <span className="author-row-title">{item.title}</span>
      <span className="author-row-stat"><Eye size={13} strokeWidth={2.2} /> {item.views}</span>
      <span className="author-row-stat"><Heart size={13} strokeWidth={2.2} /> {item.likes}</span>
      <span className="author-row-stat"><MessageCircle size={13} strokeWidth={2.2} /> {item.comments}</span>
      {item.citations > 0 && <span className="author-row-stat" style={{ color: "#e5005b" }}><Sparkles size={13} strokeWidth={2.2} /> {item.citations}</span>}
    </Link>
  );
}

export default function DoctorProfile({ onLoginRequest }) {
  const { id } = useParams();
  const { token } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);

  useEffect(() => {
    setDoctor(null);
    setNotFound(false);
    setNeedLogin(false);
    fetch(`${API_URL}/doctors/${id}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then(r => {
        if (r.status === 401) { setNeedLogin(true); return null; }
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(d => { if (d) setDoctor(d); })
      .catch(() => setNotFound(true));
  }, [id, token]);

  if (needLogin) {
    return (
      <section>
        <p style={{ color: "#888" }}>Профили врачей доступны зарегистрированным пользователям.</p>
        <button type="button" onClick={() => onLoginRequest && onLoginRequest()}>Войти или зарегистрироваться</button>
      </section>
    );
  }
  if (notFound) return <section><p style={{ color: "#888" }}>Врач не найден.</p></section>;
  if (!doctor) return <section><p style={{ color: "#888" }}>Загрузка…</p></section>;

  const specialtyLabels = (doctor.specialty || "").split(";").map(s => s.trim()).filter(Boolean)
    .map(sid => SPECIALTIES.find(s => s.id === sid)?.label || sid);
  const items = doctor.stats.items;
  const videos = items.filter(i => i.kind === "lecture");
  const texts = items.filter(i => i.kind !== "lecture");
  const isCenter = !doctor.bio && items.length > 0;

  return (
    <section className="author-page">
      <Helmet>
        <title>{doctor.display_name} — Современный Врач</title>
        <meta name="description" content={doctor.bio || `Материалы автора ${doctor.display_name} на портале Современный Врач`} />
        <meta property="og:title" content={doctor.display_name} />
        {doctor.bio && <meta property="og:description" content={doctor.bio} />}
        {doctor.avatar_url && <meta property="og:image" content={doctor.avatar_url} />}
      </Helmet>

      <div className="author-hero">
        {doctor.avatar_url
          ? <img src={doctor.avatar_url} alt="" className="author-avatar" />
          : <div className="author-avatar author-avatar-initial">{(doctor.display_name || "?")[0].toUpperCase()}</div>}
        <div className="author-hero-text">
          <h2 className="author-name">{doctor.display_name}</h2>
          <div className="author-badges">
            {specialtyLabels.map(label => <span key={label} className="spec-badge">{label}</span>)}
            {doctor.verification_status === "verified" && (
              <span className="author-verified"><BadgeCheck size={13} strokeWidth={2.4} /> Подтверждённый врач</span>
            )}
          </div>
          {doctor.bio
            ? <p className="author-bio">{doctor.bio}</p>
            : isCenter && <p className="author-bio author-bio-muted">Автор материалов портала</p>}
        </div>
      </div>

      {items.length > 0 && (
        <div className="author-stats">
          <Stat value={items.length} label={items.length === 1 ? "материал" : "материалов"} />
          <Stat value={doctor.stats.total_views} label="просмотров" />
          <Stat value={doctor.stats.total_likes} label="реакций" />
          <Stat value={doctor.stats.total_comments} label="комментариев" />
          {doctor.stats.total_citations > 0 && <Stat value={doctor.stats.total_citations} label="раз в ответах умного поиска" accent />}
        </div>
      )}

      {videos.length > 0 && (
        <>
          <h3 className="author-section">Видео и лекции <span>{videos.length}</span></h3>
          <div className="author-video-grid">
            {videos.map(v => <VideoCard key={`lecture_${v.id}`} item={v} />)}
          </div>
        </>
      )}

      {texts.length > 0 && (
        <>
          <h3 className="author-section">Статьи и клинические случаи <span>{texts.length}</span></h3>
          <div className="author-rows">
            {texts.map(t => <TextRow key={`${t.kind}_${t.id}`} item={t} />)}
          </div>
        </>
      )}

      {items.length === 0 && <p style={{ color: "#888", marginTop: 18 }}>Публикаций на портале пока нет.</p>}
    </section>
  );
}
